// Utility functions for handling image loading and Firebase Storage upload

import { storage } from "../config/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { debug } from "./logger";

/**
 * Optimizes an image for thumbnail use by resizing and compressing it
 * @param file The original image file
 * @param maxWidth Maximum width (default: 400px)
 * @param maxHeight Maximum height (default: 300px)
 * @param quality JPEG quality (0-1, default: 0.8)
 * @returns Promise<File> The optimized image file
 */
export const optimizeImageForThumbnail = async (
  file: File,
  maxWidth: number = 400,
  maxHeight: number = 300,
  quality: number = 0.8
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      try {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;

        // Draw the resized image
        ctx?.drawImage(img, 0, 0, width, height);

        // Convert to blob with compression
        canvas.toBlob(
          (blob) => {
            if (blob) {
              // Create a new file with optimized data
              const optimizedFile = new File([blob], file.name, {
                type: "image/jpeg",
                lastModified: Date.now(),
              });
              resolve(optimizedFile);
            } else {
              reject(new Error("Failed to create optimized image"));
            }
          },
          "image/jpeg",
          quality
        );
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error("Failed to load image for optimization"));
    };

    // Load the image from the file
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Validates and optimizes an image file for thumbnail upload
 * @param file The original image file
 * @returns Promise<File> The optimized image file ready for upload
 */
export const prepareThumbnailForUpload = async (file: File): Promise<File> => {
  // Check file size (5MB limit)
  const maxSize = 5 * 1024 * 1024; // 5MB in bytes
  if (file.size > maxSize) {
    throw new Error("File size must be less than 5MB");
  }

  // Check file type
  const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
  if (!allowedTypes.includes(file.type)) {
    throw new Error("Only JPEG, PNG, and WebP images are allowed");
  }

  // Optimize the image
  return await optimizeImageForThumbnail(file);
};

/**
 * Uploads a file directly to Firebase Storage and returns the download URL
 * @param file The file to upload
 * @param fileName Optional custom filename
 * @returns Promise<string> The Firebase Storage download URL
 */
export const uploadFileToFirebase = async (
  file: File,
  fileName?: string
): Promise<string> => {
  try {
    debug("Starting file upload to Firebase Storage...");
    debug("File details:", {
      name: file.name,
      size: file.size,
      type: file.type,
    });

    // Generate a unique filename if not provided
    const uniqueFileName =
      fileName ||
      `guide-layer-${Date.now()}-${Math.random().toString(36).substring(2)}.${
        file.name.split(".").pop() || "jpg"
      }`;

    debug("Generated filename:", uniqueFileName);

    // Create a reference to Firebase Storage
    const storageRef = ref(storage, `guide-layers/${uniqueFileName}`);
    debug("Storage reference created:", storageRef.fullPath);

    // Upload the file to Firebase Storage
    debug("Starting upload...");
    const uploadResult = await uploadBytes(storageRef, file);
    debug("Upload completed:", uploadResult);

    // Get the download URL
    debug("Getting download URL...");
    const downloadURL = await getDownloadURL(uploadResult.ref);
    debug("Download URL obtained:", downloadURL);

    return downloadURL;
  } catch (error: any) {
    console.error("Error uploading file to Firebase:", error);
    console.error("Error details:", {
      message: error.message,
      code: error.code,
      stack: error.stack,
    });
    throw new Error(`Failed to upload file: ${error}`);
  }
};

/**
 * Uploads an image to Firebase Storage and returns the download URL
 * @param imageUrl The original image URL to download and upload
 * @param fileName Optional custom filename
 * @returns Promise<string> The Firebase Storage download URL
 */
export const uploadImageToFirebase = async (
  imageUrl: string,
  fileName?: string
): Promise<string> => {
  try {
    // Fetch the image from the original URL
    const response = await fetch(imageUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }

    // Get the image blob
    const imageBlob = await response.blob();

    // Generate a unique filename if not provided
    const uniqueFileName =
      fileName ||
      `guide-layer-${Date.now()}-${Math.random()
        .toString(36)
        .substring(2)}.jpg`;

    // Create a reference to Firebase Storage
    const storageRef = ref(storage, `guide-layers/${uniqueFileName}`);

    // Upload the blob to Firebase Storage
    const uploadResult = await uploadBytes(storageRef, imageBlob);

    // Get the download URL
    const downloadURL = await getDownloadURL(uploadResult.ref);

    return downloadURL;
  } catch (error) {
    console.error("Error uploading image to Firebase:", error);
    throw new Error(`Failed to upload image: ${error}`);
  }
};

/**
 * Resolves an image URL by uploading it to Firebase Storage
 * @param url The original image URL
 * @returns Promise<string> The Firebase Storage URL
 */
export const resolveImageUrl = async (url: string): Promise<string> => {
  try {
    // Try to upload directly to Firebase Storage
    // If the URL is invalid or inaccessible, the fetch will fail and we'll catch the error
    return await uploadImageToFirebase(url);
  } catch (error) {
    console.error("Error resolving image URL:", error);
    throw new Error(`Failed to process image: ${error}`);
  }
};
